/**
 * Логика загрузки и обработки данных для таблицы
 */
import { RtLoaderFn } from "src/components/ReactiveTable/RtLoader";
import { RtRequest } from "src/components/ReactiveTable/RtRequest";
import { RtResponse } from "src/components/ReactiveTable/RtResponse";

import { mockData } from "./mockData";
import { User, UserFilters } from "./types";

/**
 * Применяет фильтры к данным
 */
const applyFilters = (
  data: User[],
  filters: UserFilters | undefined,
): User[] => {
  let filteredData = [...data];

  if (filters?.role && filters.role.length > 0) {
    filteredData = filteredData.filter((user) =>
      filters.role!.includes(user.role),
    );
  }

  if (filters?.status && filters.status.length > 0) {
    filteredData = filteredData.filter((user) =>
      filters.status!.includes(user.status),
    );
  }

  if (filters?.minAge !== undefined) {
    filteredData = filteredData.filter((user) => user.age >= filters.minAge!);
  }

  return filteredData;
};

/**
 * Применяет сортировку к данным
 */
const applySorting = (
  data: User[],
  sortOrder: RtRequest<UserFilters>["sortOrder"],
): User[] => {
  if (!sortOrder || sortOrder.length === 0) {
    return data;
  }

  const sortedData = [...data];
  const sort = sortOrder[0];

  if (!sort) {
    return sortedData;
  }

  sortedData.sort((a, b) => {
    const aVal = (a as unknown as Record<string, unknown>)[sort.columnKey];
    const bVal = (b as unknown as Record<string, unknown>)[sort.columnKey];
    let comparison = 0;

    if (aVal != null && bVal != null) {
      const aComparable = aVal as number | string;
      const bComparable = bVal as number | string;
      if (aComparable > bComparable) {
        comparison = 1;
      } else if (aComparable < bComparable) {
        comparison = -1;
      }
    } else if (aVal != null) {
      comparison = 1;
    } else if (bVal != null) {
      comparison = -1;
    }

    return sort.mode === "ascend" ? comparison : -comparison;
  });

  return sortedData;
};

/**
 * Применяет пагинацию к данным
 */
const applyPagination = (
  data: User[],
  page: number | undefined,
  pageSize: number | undefined,
): { rows: User[]; totalRows: number } => {
  const actualPageSize = pageSize || 20;
  const actualPage = page || 1;
  const start = (actualPage - 1) * actualPageSize;
  const end = start + actualPageSize;
  const paginatedData = data.slice(start, end);

  return {
    rows: paginatedData,
    totalRows: data.length,
  };
};

/**
 * Загрузчик данных для таблицы
 * Симулирует задержку загрузки и применяет фильтры, сортировку и пагинацию
 */
export const createDataLoader =
  (): RtLoaderFn<User, UserFilters> =>
  async (request: RtRequest<UserFilters>): Promise<RtResponse<User>> => {
    // Симуляция загрузки данных
    await new Promise<void>((resolve) => {
      setTimeout(resolve, 300);
    });

    const { page, pageSize, sortOrder, filters } = request;

    let processedData = applyFilters(mockData, filters);
    processedData = applySorting(processedData, sortOrder);

    return applyPagination(processedData, page, pageSize);
  };
